import { z } from 'zod/v3';
import { CocoEvent } from '@cotera/client/app/etc';
import React from 'react';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type { AskResponse } from '@cotera/client/v0/actions/framework/types';
import type { ActionTrackingContext } from '@cotera/client/v0/actions/framework/action-tracking';
import { ok, err } from 'neverthrow';
import { ResourceType } from '@cotera/api';
import type { QueryClient } from '@tanstack/react-query';
import { CocoChatModel } from '../../chat/models/coco-chat.model';
import { AgentModel } from '../../agent/agent.model';
import { DocumentModel } from '../../document/document.model';
import { ResourceSelectionModel } from '../selection/resource-selection.model';
import { FolderContentsPageModel } from '../../folder/folder-contents-page.model';
import {
  moveResourcesToFolder,
  type MoveToFolderClient,
} from './move-resources-to-folder';
import {
  FolderPickerAskView,
  type ModalManager,
  type TargetResource,
} from '../folder-picker-ask-view';

const moveToFolderParamsSchema = z.object({
  /** Target folder id, or `null` to move the resource(s) out to the top level. */
  folderId: z.string().uuid().nullable(),
});

type Params = z.infer<typeof moveToFolderParamsSchema>;

/** Returns all target resources (single from page model or multiple from list selection). */
function getTargetResources(context: ExecuteContext): TargetResource[] {
  const selection = context.targetedModelOfType(ResourceSelectionModel);
  if (selection !== null && selection.selections.length > 0) {
    return selection.selections.map((resource) => ({
      type: resource.resourceType,
      id: resource.resourceId,
    }));
  }
  const chatModel = context.targetedModelOfType(CocoChatModel);
  if (chatModel !== null) {
    return [{ type: ResourceType.Chat, id: chatModel.conversationId }];
  }
  const agentModel = context.targetedModelOfType(AgentModel);
  if (agentModel !== null) {
    return [{ type: ResourceType.Agent, id: agentModel.agentId }];
  }
  const documentModel = context.targetedModelOfType(DocumentModel);
  if (documentModel !== null) {
    return [{ type: ResourceType.Document, id: documentModel.documentId }];
  }
  const folderModel =
    context.targetedModelOfType(FolderContentsPageModel) ??
    context.getInScopeModelOfType(FolderContentsPageModel);
  if (folderModel !== null && folderModel.folderId !== null) {
    return [{ type: ResourceType.Folder, id: folderModel.folderId }];
  }
  return [];
}

export class MoveToFolderAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Move to Folder';
  /**
   * Acts on whatever resource is targeted, so it belongs to that resource's own
   * menu, not to a workspace-wide search. It stays registered and invokable —
   * `discrete` only keeps it out of the palette's list.
   */
  discrete = true;

  description = 'Move this resource into another folder';
  inputSchema = moveToFolderParamsSchema;
  group = 'app';
  icon = 'folder';

  private _lastMovedResources: TargetResource[] = [];

  constructor(
    private readonly client: MoveToFolderClient,
    private readonly queryClient: Pick<QueryClient, 'invalidateQueries'>,
    private readonly orgId: string,
    private readonly modalManager: ModalManager
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    if (
      context.hasTargetedModelOfType(CocoChatModel) ||
      context.hasTargetedModelOfType(AgentModel)
    ) {
      return true;
    }
    const selection = context.targetedModelOfType(ResourceSelectionModel);
    if (selection !== null && selection.selections.length > 0) {
      return true;
    }
    const folderModel = context.targetedModelOfType(FolderContentsPageModel);
    return folderModel !== null && folderModel.folderId !== null;
  }

  getPriority(context: ApplicableContext): number {
    const selection = context.targetedModelOfType(ResourceSelectionModel);
    if (selection !== null) {
      return 20;
    }
    return 0;
  }

  shouldAsk(): boolean {
    return true;
  }

  async execute(
    payload: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const resources = getTargetResources(context);
    this._lastMovedResources = resources;
    if (resources.length === 0) {
      return err([{ t: 'error', message: 'No resource in scope' }]);
    }

    const folderPageModel = context.getInScopeModelOfType(
      FolderContentsPageModel
    );
    const selectionForOptimistic = context.targetedModelOfType(
      ResourceSelectionModel
    );
    const optimisticIds =
      selectionForOptimistic !== null &&
      selectionForOptimistic.selections.length > 0
        ? selectionForOptimistic.selections.map((s) => s.resourceId)
        : resources.map((r) => r.id);

    const result = await moveResourcesToFolder({
      client: this.client,
      queryClient: this.queryClient,
      orgId: this.orgId,
      resources,
      folderId: payload.folderId,
      optimisticIds,
      folderPageModel,
    });

    if (result.isErr()) {
      return err([{ t: 'error', message: result.error }]);
    }

    const selection = context.targetedModelOfType(ResourceSelectionModel);
    if (selection !== null) {
      context.markAsNotTargeted(selection);
    }
    return ok({});
  }

  track({
    payload,
  }: ActionTrackingContext<
    Params,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const movedResources = this._lastMovedResources;
    if (movedResources.length === 0) {
      return null;
    }
    const resourceTypes = [...new Set(movedResources.map((r) => r.type))];
    return {
      event: CocoEvent.ResourceMovedToFolder,
      properties: {
        destinationFolderId: payload.folderId,
        resourceIds: movedResources.map((r) => r.id),
        resourceTypes,
        count: movedResources.length,
      },
    };
  }

  ask(
    _params: Partial<Params>,
    context: ExecuteContext
  ): AskResponse<Params, { t: string; message?: string }[]> {
    const resources = getTargetResources(context);
    const targetResource = resources[0] ?? null;
    // When invoked from inside a folder's contents page, always surface the
    // "Move to top level" option even for targets whose folder membership the
    // picker can't infer from metadata (e.g. multi-select or folder rows).
    const inScopeFolderPage = context.getInScopeModelOfType(
      FolderContentsPageModel
    );
    const isInsideFolder =
      inScopeFolderPage !== null && inScopeFolderPage.folderId !== null;
    return {
      view: ({ onRespond, onCancel }) => {
        const handleCancel = async () => {
          await onCancel();
          this.modalManager.pop();
        };
        this.modalManager.push({
          view: () => (
            <FolderPickerAskView
              modalManager={this.modalManager}
              targetResource={targetResource}
              onRespond={async (params) => {
                await onRespond(params);
                this.modalManager.pop();
              }}
              onMoveToTopLevel={async () => {
                await onRespond({ folderId: null });
                this.modalManager.pop();
              }}
              alwaysShowMoveToTopLevel={isInsideFolder}
              onCancel={handleCancel}
            />
          ),
          onClose: handleCancel,
        });
      },
    };
  }
}
